"use client"

import * as React from "react"
import { LeoBrandBackdrop } from "@exxatdesignux/ui/components/leo-brand-backdrop"
import { LeoGenerationStatus } from "@exxatdesignux/ui/components/leo-generation-surface"
import type { LeoGenerationState } from "@exxatdesignux/ui/lib/leo-generation"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  isCategoryVisibleInSearch,
  type ExamCategory,
  type ExamCategoryTreeNode,
} from "@/lib/exam-categories"
import { LeoIcon } from "@/components/ui/leo-icon"
import { cn } from "@/lib/utils"

/** A row Leo is working on: inside a parent (first or last), or right after a row. */
export interface TreeLeoRowSpec {
  key: string
  parentId: string | null
  afterId?: string
  atStart?: boolean
  label: string
  state: LeoGenerationState
}
const TreeLeoRowsContext = React.createContext<readonly TreeLeoRowSpec[]>([])

/** Leo's row in the tree: the package's brand backdrop and status line, at the row's depth. */
function TreeLeoRow({ row, depth }: { row: TreeLeoRowSpec; depth: number }) {
  const ref = React.useRef<HTMLLIElement>(null)
  React.useEffect(() => {
    // Where Leo is working stays in view, clear of the Leo bar at the foot.
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ref.current?.scrollIntoView?.({ block: "center", behavior: reduce ? "auto" : "smooth" })
  }, [])
  return (
    <li ref={ref} className="list-none" data-leo-state={row.state}>
      <div className="relative flex min-h-8 items-center overflow-hidden rounded-md" style={rowPadding(depth)}>
        <LeoBrandBackdrop />
        <span className="h-8 w-[var(--tree-chevron)] shrink-0" aria-hidden />
        <LeoGenerationStatus state={row.state} label={row.label} className="relative z-10 min-w-0 flex-1 pe-3" />
      </div>
    </li>
  )
}

/** Leo rows that belong inside `parentId` (first or last) or right after `afterId`. */
function useLeoRows() {
  const rows = React.useContext(TreeLeoRowsContext)
  return {
    first: (parentId: string | null) => rows.filter((row) => row.parentId === parentId && !row.afterId && row.atStart),
    last: (parentId: string | null) => rows.filter((row) => row.parentId === parentId && !row.afterId && !row.atStart),
    after: (id: string) => rows.filter((row) => row.afterId === id),
  }
}

/** Inline rename: which row is being renamed, and how to start, save or leave it. */
type TreeRename = {
  editingId: string | null
  start: (id: string) => void
  commit: (id: string, name: string) => void
  cancel: () => void
}
const TreeRenameContext = React.createContext<TreeRename | null>(null)

/** The row's name as a text field. Enter or leaving saves; Escape puts the old name back. */
function RenameField({ node, icon, iconClassName }: { node: ExamCategoryTreeNode; icon: string; iconClassName: string }) {
  const rename = React.useContext(TreeRenameContext)!
  const [value, setValue] = React.useState(node.name)
  const done = React.useRef(false)
  const save = () => {
    if (done.current) return
    done.current = true
    if (value.trim() && value.trim() !== node.name) rename.commit(node.id, value.trim())
    else rename.cancel()
  }
  return (
    <div className="flex min-h-8 min-w-0 flex-1 items-center gap-2 py-0.5 pe-3">
      <i className={cn("fa-light shrink-0 text-sm", icon, iconClassName)} aria-hidden="true" />
      <input
        aria-label={`Rename ${node.name}`}
        autoFocus
        value={value}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setValue(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === "Enter") save()
          if (event.key === "Escape") {
            done.current = true
            rename.cancel()
          }
        }}
        draggable={false}
        onDragStart={(event) => event.preventDefault()}
        className="h-7 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  )
}

/** The top level, as a parent for inline add. */
export const TREE_ROOT = "__tree-root__"

/**
 * Inline add: which branch is taking new rows (a category's id, or TREE_ROOT),
 * and how to start, save or leave it. Each open branch ends with "+ Add", and
 * the rows typed there land inside it, several at a time.
 */
export type TreeInlineAdd = {
  addingId: string | null
  start: (parentId: string) => void
  cancel: () => void
  commit: (parentId: string, names: string[]) => void
  /** What a row at this depth is called: "domain", "skill", "category". */
  noun: (depth: number) => string
  /** Levels the tree allows; no add rows at or below this depth. */
  maxDepth: number
}
const TreeAddContext = React.createContext<TreeInlineAdd | null>(null)

/**
 * The foot of a branch (or the whole tree): "+ Add …" at rest, and the rows
 * being added once it is clicked. Enter opens another row, a pasted list
 * fills one row per line, Cmd/Ctrl+Enter adds them all, Escape leaves.
 */
function TreeAddRows({ parentId, depth, idle = true }: { parentId: string; depth: number; idle?: boolean }) {
  const add = React.useContext(TreeAddContext)
  if (!add || depth >= add.maxDepth) return null
  if (add.addingId === parentId) return <TreeAddEditor add={add} parentId={parentId} depth={depth} />
  if (!idle) return null
  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => add.start(parentId)}
        style={rowPadding(depth)}
        className="flex min-h-8 w-full items-center gap-2 rounded-md text-start text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <span className="flex h-8 w-[var(--tree-chevron)] shrink-0 items-center justify-center" aria-hidden="true">
          <i className="fa-light fa-plus text-xs" />
        </span>
        Add {add.noun(depth)}
      </button>
    </li>
  )
}

let nextAddRowId = 1

function TreeAddEditor({ add, parentId, depth }: { add: TreeInlineAdd; parentId: string; depth: number }) {
  const [rows, setRows] = React.useState(() => [{ id: nextAddRowId++, name: "" }])
  const refs = React.useRef(new Map<number, HTMLInputElement>())
  const [focusId, setFocusId] = React.useState<number | null>(rows[0]!.id)
  React.useEffect(() => {
    if (focusId === null) return
    refs.current.get(focusId)?.focus()
    setFocusId(null)
  }, [focusId])
  const names = rows.map((row) => row.name.trim()).filter(Boolean)
  const noun = add.noun(depth)
  const submit = () => {
    if (names.length > 0) add.commit(parentId, names)
  }

  return (
    <li className="list-none py-1" style={rowPadding(depth)}>
      <div role="group" aria-label={`Add ${noun}`} className="ms-[var(--tree-chevron)] flex flex-col gap-1.5 rounded-lg bg-muted/40 p-2">
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {rows.map((row, index) => (
            <li key={row.id} className="flex items-center gap-2">
              <i className="fa-light fa-tag w-4 shrink-0 text-center text-sm text-muted-foreground" aria-hidden="true" />
              <input
                ref={(node) => {
                  if (node) refs.current.set(row.id, node)
                  else refs.current.delete(row.id)
                }}
                aria-label={`New ${noun} ${index + 1}`}
                placeholder={index === 0 ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} name, or paste a list` : "Name"}
                value={row.name}
                draggable={false}
                onDragStart={(event) => event.preventDefault()}
                onChange={(event) => setRows((current) => current.map((r) => (r.id === row.id ? { ...r, name: event.target.value } : r)))}
                onPaste={(event) => {
                  const text = event.clipboardData.getData("text")
                  if (!/\n/.test(text.trim())) return
                  event.preventDefault()
                  const pasted = text
                    .split(/\r?\n/)
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((name) => ({ id: nextAddRowId++, name }))
                  setRows((current) => {
                    const at = current.findIndex((r) => r.id === row.id)
                    const head = row.name.trim() ? [row] : []
                    return [...current.slice(0, at), ...head, ...pasted, ...current.slice(at + 1)]
                  })
                }}
                onKeyDown={(event) => {
                  event.stopPropagation()
                  if (event.key === "Escape") {
                    event.preventDefault()
                    add.cancel()
                  } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault()
                    submit()
                  } else if (event.key === "Enter") {
                    event.preventDefault()
                    const next = rows[index + 1]
                    if (next) setFocusId(next.id)
                    else if (row.name.trim()) {
                      const fresh = { id: nextAddRowId++, name: "" }
                      setRows((current) => [...current, fresh])
                      setFocusId(fresh.id)
                    }
                  }
                }}
                className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                aria-label={`Remove row ${index + 1}`}
                disabled={rows.length === 1}
                onClick={() => setRows((current) => current.filter((r) => r.id !== row.id))}
              >
                <i className="fa-light fa-xmark" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center justify-between gap-2 ps-6">
          <p className="m-0 text-xs text-muted-foreground">Enter for another row. Paste a list to add several.</p>
          <div className="ms-auto flex items-center gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={add.cancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={names.length === 0} onClick={submit}>
              {names.length > 1 ? `Add ${names.length}` : `Add ${noun}`}
            </Button>
          </div>
        </div>
      </div>
    </li>
  )
}

/** Double-click or F2 on a row's name starts a rename, when the tree allows it. */
function useRenameTrigger(id: string) {
  const rename = React.useContext(TreeRenameContext)
  if (!rename) return { editing: false, triggerProps: {} }
  return {
    editing: rename.editingId === id,
    triggerProps: {
      onDoubleClick: () => rename.start(id),
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === "F2") {
          event.preventDefault()
          rename.start(id)
        }
      },
    },
  }
}

function filterTree(
  nodes: ExamCategoryTreeNode[],
  categories: ExamCategory[],
  query: string,
): ExamCategoryTreeNode[] {
  const trimmed = query.trim()
  if (!trimmed) return nodes
  return nodes
    .map((node) => ({
      ...node,
      children: filterTree(node.children, categories, trimmed),
    }))
    .filter(
      (node) =>
        isCategoryVisibleInSearch(categories, node.id, trimmed) ||
        node.children.length > 0,
    )
}

function isBranchOpen(
  nodeId: string,
  depth: number,
  expandedIds: Record<string, boolean>,
  defaultOpenRoots: boolean,
): boolean {
  if (nodeId in expandedIds) return expandedIds[nodeId]!
  // A picker opens only along what is selected. A 3,000-node tree that opens
  // every top-level branch by default is a wall to scroll before the author
  // finds anything. The settings tree, worked from the top, keeps roots open.
  return defaultOpenRoots && depth === 0
}

/**
 * Row geometry, defined once.
 *
 * The indent, the width of the chevron and the left edge are three tokens on
 * the tree itself. A row's padding, a chevron's width and the guide line that
 * runs down from a chevron all read the same three, so the line can only ever
 * sit on the middle of the arrow it hangs from. Nothing repeats a number.
 */
const TREE_GEOMETRY = {
  "--tree-base": "0.25rem",
  "--tree-indent": "0.75rem",
  "--tree-chevron": "2rem",
} as React.CSSProperties

/**
 * Quick actions float over the row's right end on hover or focus, so the row
 * keeps its full width at rest. The inner layer repaints the row's own fill.
 */
function RowActions({ selected, children }: { selected: boolean; children: React.ReactNode }) {
  return (
    <span className="pointer-events-none absolute inset-y-0 end-0 z-10 flex rounded-e-md bg-background opacity-0 transition-opacity group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100 group-hover/row:pointer-events-auto group-hover/row:opacity-100">
      <span className={cn("flex items-center gap-0.5 rounded-e-md ps-2 pe-1", selected ? "bg-accent" : "bg-muted/50")}>{children}</span>
    </span>
  )
}

/** Bulk selection: which rows are checked, and how to check one. */
type TreeCheck = { checked: ReadonlySet<string>; onCheck: (id: string) => void }
const TreeCheckContext = React.createContext<TreeCheck | null>(null)

/**
 * A row's bulk checkbox, at the far start of the gutter. Seen on hover, and on
 * every row once anything is checked, so a selection is never hidden.
 */
function RowCheck({ node }: { node: ExamCategoryTreeNode }) {
  const check = React.useContext(TreeCheckContext)
  if (!check) return null
  const on = check.checked.has(node.id)
  return (
    <span
      className={cn(
        "absolute inset-y-0 z-10 flex w-5 items-center justify-center transition-opacity",
        on || check.checked.size > 0 ? "opacity-100" : "opacity-0 group-hover/row:opacity-100 focus-within:opacity-100",
      )}
      style={{ insetInlineStart: "calc(var(--tree-depth) * var(--tree-indent))" }}
    >
      <Checkbox checked={on} onCheckedChange={() => check.onCheck(node.id)} aria-label={`Select ${node.name}`} />
    </span>
  )
}

/** The drag grip, in the gutter before the chevron, seen only on hover. */
function DragGrip() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-y-0 flex w-4 items-center justify-center text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100"
      style={{ insetInlineStart: "calc(var(--tree-base) - 1rem + var(--tree-depth) * var(--tree-indent))" }}
    >
      <i className="fa-light fa-grip-dots-vertical text-xs" />
    </span>
  )
}

/** Left edge of a row at this depth. */
const rowPadding = (depth: number): React.CSSProperties =>
  ({
    "--tree-depth": depth,
    paddingInlineStart: "calc(var(--tree-base) + var(--tree-depth) * var(--tree-indent))",
  }) as React.CSSProperties

/**
 * The arrow, drawn so its ink is centred on its own box.
 *
 * A font glyph's ink sits off-centre in its advance, and rotating it about the
 * box moves that offset: the arrow shifted by a pixel between open and closed
 * and the line beside it looked misplaced. This path is symmetric about (6, 6),
 * so turning it a quarter leaves its middle exactly where the line runs.
 */
function BranchChevron() {
  return (
    <svg
      viewBox="0 0 12 12"
      className="block size-3 transition-transform duration-150 group-data-[state=open]/collapsible:rotate-90"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.75 2 L8.25 6 L3.75 10" />
    </svg>
  )
}

/**
 * The line down a branch: one element for the whole group of children, centred
 * on the chevron of the row above it (its left edge plus half the chevron).
 * One continuous line per branch rather than a fragment in every row.
 */
function BranchGuide({ depth }: { depth: number }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 w-px bg-border/70"
      style={
        {
          "--tree-depth": depth,
          insetInlineStart:
            "calc(var(--tree-base) + var(--tree-depth) * var(--tree-indent) + var(--tree-chevron) / 2)",
          marginInlineStart: "-0.5px",
        } as React.CSSProperties
      }
    />
  )
}

/** Drag-and-drop between siblings, shared by every row of one tree. */
type TreeDrag = {
  dragId: string | null
  overId: string | null
  /** Row props that make a row draggable and a drop target for its siblings. */
  rowProps: (node: ExamCategoryTreeNode) => React.HTMLAttributes<HTMLDivElement> & { draggable?: boolean }
}

/** How many suggested nodes sit below each node, worked out once for the whole tree. */
function suggestedBelowCounts(
  tree: ExamCategoryTreeNode[],
  suggestedIds: ReadonlySet<string> | undefined,
): Map<string, number> {
  const counts = new Map<string, number>()
  if (!suggestedIds || suggestedIds.size === 0) return counts
  const walk = (node: ExamCategoryTreeNode): number => {
    let below = 0
    for (const child of node.children) {
      below += (suggestedIds.has(child.id) ? 1 : 0) + walk(child)
    }
    counts.set(node.id, below)
    return below
  }
  tree.forEach(walk)
  return counts
}

/** The count of leading rows needed to include every selected or open one. */
function mustShow(
  nodes: ExamCategoryTreeNode[],
  selected: ReadonlySet<string> | undefined,
  expanded: Record<string, boolean>,
): number {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const id = nodes[i]!.id
    if (selected?.has(id) || expanded[id]) return i + 1
  }
  return 0
}

/** Rows drawn per list before "Show more". A 6,000-node tree stays a few hundred rows. */
const PAGE_SIZE = 100

function PagedList({
  total,
  minLimit = 0,
  children,
}: {
  total: number
  /** Rows that must be drawn: the selection and open branches are never hidden. */
  minLimit?: number
  children: (limit: number) => React.ReactNode
}) {
  const [limit, setLimit] = React.useState(PAGE_SIZE)
  const shown = Math.max(limit, minLimit)
  return (
    <>
      {children(shown)}
      {total > shown ? (
        <li className="list-none px-2 py-1">
          <button
            type="button"
            onClick={() => setLimit(shown + PAGE_SIZE)}
            className="rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Show {Math.min(PAGE_SIZE, total - shown)} more ({total - shown} not shown)
          </button>
        </li>
      ) : null}
    </>
  )
}

/**
 * Leo's suggestion, marked where the author is looking. In a 3,000-node tree
 * nobody hunts through collapsed folders, so a collapsed parent that leads to
 * a suggestion says so, and the count leads the eye down.
 */
function SuggestionMarker({ label }: { label: string }) {
  return (
    <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full bg-brand/10 px-1.5 py-0.5 text-[11px] font-medium text-brand">
      <LeoIcon variant="ambient" size="xs" ariaLabel="" />
      {label}
    </span>
  )
}

function CategoryTreeItem({
  node,
  depth,
  selectedId,
  selectedSet,
  suggestedBelow,
  showCheckbox,
  expandedIds,
  suggestedIds,
  defaultOpenRoots,
  renderMeta,
  renderActions,
  drag,
  onToggleExpand,
  onSelect,
}: {
  drag?: TreeDrag
  selectedSet?: ReadonlySet<string>
  suggestedBelow: Map<string, number>
  renderMeta?: (node: ExamCategoryTreeNode) => React.ReactNode
  /** Quick actions beside the row, shown on hover or focus. */
  renderActions?: (node: ExamCategoryTreeNode) => React.ReactNode
  defaultOpenRoots: boolean
  node: ExamCategoryTreeNode
  depth: number
  selectedId: string | null
  showCheckbox?: boolean
  expandedIds: Record<string, boolean>
  suggestedIds?: ReadonlySet<string>
  onToggleExpand: (id: string, open: boolean) => void
  onSelect: (id: string) => void
}) {
  const children = node.children
  const hasChildren = children.length > 0
  const isOpen = isBranchOpen(node.id, depth, expandedIds, defaultOpenRoots)
  const isSelected = selectedSet ? selectedSet.has(node.id) : selectedId === node.id
  const icon = hasChildren ? (isOpen ? "fa-folder-open" : "fa-folder") : "fa-tag"
  const { editing, triggerProps } = useRenameTrigger(node.id)
  const leoRows = useLeoRows()
  const iconTone = depth === 0 || hasChildren ? "text-brand" : "text-muted-foreground"
  const selectControl = showCheckbox ? (
    <div className="flex size-8 shrink-0 items-center justify-center pe-0.5">
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onSelect(node.id)}
        aria-label={`Select ${node.name}`}
      />
    </div>
  ) : null

  if (!hasChildren) {
    return (
      <li className="list-none">
        <div
          className={cn(
            "group/row relative flex min-h-8 w-full min-w-0 items-start rounded-md",
            isSelected
              ? "bg-accent font-medium text-accent-foreground"
              : "text-foreground hover:bg-muted/50",
            drag?.overId === node.id && "ring-2 ring-inset ring-brand/50",
            drag?.dragId === node.id && "opacity-50",
          )}
          style={rowPadding(depth)}
          {...drag?.rowProps(node)}
        >
          <span className="h-8 w-[var(--tree-chevron)] shrink-0" aria-hidden />
          {editing ? (
            <RenameField node={node} icon={icon} iconClassName={iconTone} />
          ) : (
          <button
            type="button"
            {...triggerProps}
            onClick={() => onSelect(node.id)}
            aria-current={isSelected ? "true" : undefined}
            className={cn(
              "flex min-h-8 min-w-0 flex-1 items-start gap-2 py-1.5 pe-3 text-start text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            )}
          >
            <i
              className={cn(
                "fa-light mt-0.5 shrink-0 text-sm",
                icon,
                depth === 0 || hasChildren ? "text-brand" : "text-muted-foreground",
                isSelected && "fa-solid",
              )}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 break-words leading-snug">{node.name}</span>
            {suggestedIds?.has(node.id) ? (
              <SuggestionMarker label="Leo suggests" />
            ) : null}
            {renderMeta?.(node)}
          </button>
          )}
          {renderActions && !editing ? (
            <RowActions selected={isSelected}>{renderActions(node)}</RowActions>
          ) : null}
          {drag ? <DragGrip /> : null}
          <RowCheck node={node} />
          {selectControl}
        </div>
        {/* A leaf taking its first sub-categories: the add rows open under it. */}
        <ul className="m-0 p-0" role="group" aria-label={`${node.name} subcategories`}>
          <TreeAddRows parentId={node.id} depth={depth + 1} idle={false} />
        </ul>
      </li>
    )
  }

  return (
    <li className="list-none">
      <Collapsible
        open={isOpen}
        onOpenChange={(open) => onToggleExpand(node.id, open)}
        className="group/collapsible"
      >
        <div
          className={cn(
            "group/row relative flex min-h-8 items-start rounded-md",
            isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/50",
            drag?.overId === node.id && "ring-2 ring-inset ring-brand/50",
            drag?.dragId === node.id && "opacity-50",
          )}
          style={rowPadding(depth)}
          {...drag?.rowProps(node)}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
              aria-expanded={isOpen}
              className={cn(
                "flex h-8 w-[var(--tree-chevron)] shrink-0 items-center justify-center rounded-md text-muted-foreground",
                "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "data-[state=open]:bg-transparent data-[state=open]:text-muted-foreground",
              )}
            >
              <BranchChevron />
            </button>
          </CollapsibleTrigger>

          {editing ? (
            <RenameField node={node} icon={icon} iconClassName={iconTone} />
          ) : (
          <button
            type="button"
            {...triggerProps}
            onClick={() => onSelect(node.id)}
            aria-current={isSelected ? "true" : undefined}
            className={cn(
              "flex min-w-0 flex-1 items-start gap-2 py-1.5 pe-3 text-start text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
              isSelected ? "font-medium" : "text-foreground",
            )}
          >
            <i
              className={cn(
                "fa-light mt-0.5 shrink-0 text-sm",
                icon,
                depth === 0 || hasChildren ? "text-brand" : "text-muted-foreground",
                isSelected && "fa-solid",
              )}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 break-words leading-snug">{node.name}</span>
            {suggestedIds?.has(node.id) ? (
              <SuggestionMarker label="Leo suggests" />
            ) : !isOpen && (suggestedBelow.get(node.id) ?? 0) > 0 ? (
              <SuggestionMarker
                label={`${suggestedBelow.get(node.id) ?? 0} suggested`}
              />
            ) : null}
            {renderMeta ? (
              renderMeta(node)
            ) : (
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {children.length}
              </span>
            )}
          </button>
          )}
          {renderActions && !editing ? (
            <RowActions selected={isSelected}>{renderActions(node)}</RowActions>
          ) : null}
          {drag ? <DragGrip /> : null}
          <RowCheck node={node} />
          {selectControl}
        </div>

        <CollapsibleContent className="overflow-hidden">
          <ul className="relative flex flex-col gap-0" role="group" aria-label={`${node.name} subcategories`}>
            <BranchGuide depth={depth} />
            {leoRows.first(node.id).map((row) => <TreeLeoRow key={row.key} row={row} depth={depth + 1} />)}
            <PagedList total={children.length} minLimit={mustShow(children, selectedSet, expandedIds)}>
              {(limit) =>
                children.slice(0, limit).map((child) => (
                  <React.Fragment key={child.id}>
                  <CategoryTreeItem
                    key={child.id}
                    node={child}
                    depth={depth + 1}
                    selectedId={selectedId}
                    selectedSet={selectedSet}
                    suggestedBelow={suggestedBelow}
                    showCheckbox={showCheckbox}
                    expandedIds={expandedIds}
                    suggestedIds={suggestedIds}
                    renderMeta={renderMeta}
                    renderActions={renderActions}
                    drag={drag}
                    defaultOpenRoots={defaultOpenRoots}
                    onToggleExpand={onToggleExpand}
                    onSelect={onSelect}
                  />
                  {leoRows.after(child.id).map((row) => <TreeLeoRow key={row.key} row={row} depth={depth + 1} />)}
                  </React.Fragment>
                ))
              }
            </PagedList>
            {leoRows.last(node.id).map((row) => <TreeLeoRow key={row.key} row={row} depth={depth + 1} />)}
            <TreeAddRows parentId={node.id} depth={depth + 1} />
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

const NO_LEO_ROWS: readonly TreeLeoRowSpec[] = []

export function ExamCategoryTree({
  tree,
  categories,
  selectedId,
  selectedIds,
  showCheckbox = false,
  expandedIds,
  searchQuery = "",
  suggestedIds,
  defaultOpenRoots = false,
  renderMeta,
  renderActions,
  onReorder,
  onRename,
  renameRequest,
  leoRows = NO_LEO_ROWS,
  inlineAdd,
  checkedIds,
  onCheck,
  onToggleExpand,
  onSelect,
}: {
  /** Add rows in place at every level. Omit to keep adding elsewhere. */
  inlineAdd?: TreeInlineAdd
  /** Bulk selection, separate from the open row: a checkbox in each row's gutter. */
  checkedIds?: ReadonlySet<string>
  onCheck?: (id: string) => void
  /** Rows Leo is working on, shown in place. */
  leoRows?: readonly TreeLeoRowSpec[]
  /** Rename a row in place (double-click, F2). Omit to keep names read-only here. */
  onRename?: (id: string, name: string) => void
  /** Start renaming this row, e.g. one just added. A new object restarts it. */
  renameRequest?: { id: string } | null
  /** Trailing content on a row, e.g. how many course categories map to it. */
  renderMeta?: (node: ExamCategoryTreeNode) => React.ReactNode
  /** Quick actions beside each row, shown on hover or focus. */
  renderActions?: (node: ExamCategoryTreeNode) => React.ReactNode
  /** Drag a row onto a sibling to take its place. Omit to turn dragging off. */
  onReorder?: (dragId: string, targetId: string) => void
  /** Open the top-level branches until told otherwise. Pickers leave this off. */
  defaultOpenRoots?: boolean
  suggestedIds?: ReadonlySet<string>
  tree: ExamCategoryTreeNode[]
  categories: ExamCategory[]
  selectedId?: string | null
  selectedIds?: string[]
  showCheckbox?: boolean
  expandedIds: Record<string, boolean>
  searchQuery?: string
  onToggleExpand: (id: string, open: boolean) => void
  onSelect: (id: string) => void
}) {
  const filteredTree = React.useMemo(
    () => filterTree(tree, categories, searchQuery),
    [categories, searchQuery, tree],
  )

  const [editingId, setEditingId] = React.useState<string | null>(null)
  React.useEffect(() => {
    if (renameRequest) setEditingId(renameRequest.id)
  }, [renameRequest])
  const rename = React.useMemo<TreeRename | null>(
    () =>
      onRename
        ? {
            editingId,
            start: setEditingId,
            commit: (id, name) => {
              onRename(id, name)
              setEditingId(null)
            },
            cancel: () => setEditingId(null),
          }
        : null,
    [editingId, onRename],
  )
  const check = React.useMemo<TreeCheck | null>(
    () => (onCheck ? { checked: checkedIds ?? new Set(), onCheck } : null),
    [checkedIds, onCheck],
  )
  const [dragId, setDragId] = React.useState<string | null>(null)
  const [overId, setOverId] = React.useState<string | null>(null)
  const parentOf = React.useMemo(() => new Map(categories.map((c) => [c.id, c.parentId])), [categories])
  const drag = React.useMemo<TreeDrag | undefined>(() => {
    if (!onReorder) return undefined
    const reset = () => {
      setDragId(null)
      setOverId(null)
    }
    return {
      dragId,
      overId,
      rowProps: (node) => ({
        draggable: true,
        onDragStart: (event) => {
          event.stopPropagation()
          event.dataTransfer.effectAllowed = "move"
          event.dataTransfer.setData("text/plain", node.id)
          setDragId(node.id)
        },
        onDragOver: (event) => {
          // Only a sibling is a drop target: same parent, so the tree's shape holds.
          if (!dragId || dragId === node.id || parentOf.get(dragId) !== parentOf.get(node.id)) return
          event.preventDefault()
          event.stopPropagation()
          event.dataTransfer.dropEffect = "move"
          if (overId !== node.id) setOverId(node.id)
        },
        onDragLeave: () => {
          if (overId === node.id) setOverId(null)
        },
        onDrop: (event) => {
          event.preventDefault()
          event.stopPropagation()
          if (dragId && dragId !== node.id) onReorder(dragId, node.id)
          reset()
        },
        onDragEnd: reset,
      }),
    }
  }, [dragId, onReorder, overId, parentOf])

  const selectedSet = React.useMemo(
    () => (selectedIds ? new Set(selectedIds) : undefined),
    [selectedIds],
  )
  const suggestedBelow = React.useMemo(
    () => suggestedBelowCounts(filteredTree, suggestedIds),
    [filteredTree, suggestedIds],
  )

  return (
    <TreeRenameContext.Provider value={rename}>
    <TreeAddContext.Provider value={inlineAdd ?? null}>
    <TreeLeoRowsContext.Provider value={leoRows}>
    <TreeCheckContext.Provider value={check}>
    <ul
      role="tree"
      aria-label="Category tree"
      className="flex flex-col gap-0"
      // The gutter holds the bulk checkbox and the drag grip, when each is on.
      style={
        onReorder || onCheck
          ? ({ ...TREE_GEOMETRY, "--tree-base": onCheck ? (onReorder ? "2.25rem" : "1.5rem") : "1.25rem" } as React.CSSProperties)
          : TREE_GEOMETRY
      }
    >
      {leoRows.filter((row) => row.parentId === null && !row.afterId && row.atStart).map((row) => (
        <TreeLeoRow key={row.key} row={row} depth={0} />
      ))}
      {filteredTree.length === 0 && leoRows.length === 0 && inlineAdd?.addingId !== TREE_ROOT ? (
        <li className="list-none px-2 py-4 text-sm text-muted-foreground">
          No categories match.
        </li>
      ) : (
        <PagedList total={filteredTree.length} minLimit={mustShow(filteredTree, selectedSet, expandedIds)}>
          {(limit) =>
            filteredTree.slice(0, limit).map((node) => (
              <React.Fragment key={node.id}>
              <CategoryTreeItem
                key={node.id}
                node={node}
                depth={0}
                selectedId={selectedId ?? null}
                selectedSet={selectedSet}
                suggestedBelow={suggestedBelow}
                showCheckbox={showCheckbox}
                expandedIds={expandedIds}
                suggestedIds={suggestedIds}
                renderMeta={renderMeta}
                renderActions={renderActions}
                drag={drag}
                defaultOpenRoots={defaultOpenRoots}
                onToggleExpand={onToggleExpand}
                onSelect={onSelect}
              />
              {leoRows.filter((row) => row.afterId === node.id).map((row) => <TreeLeoRow key={row.key} row={row} depth={0} />)}
              </React.Fragment>
            ))
          }
        </PagedList>
      )}
      {leoRows.filter((row) => row.parentId === null && !row.afterId && !row.atStart).map((row) => (
        <TreeLeoRow key={row.key} row={row} depth={0} />
      ))}
      <TreeAddRows parentId={TREE_ROOT} depth={0} />
    </ul>
    </TreeCheckContext.Provider>
    </TreeLeoRowsContext.Provider>
    </TreeAddContext.Provider>
    </TreeRenameContext.Provider>
  )
}
